<template>
  <div class="min-h-screen">
    <SiteHeader :page-name="'Project View'" />
    <div v-if="draw?.project.locked" class="h-12 w-full sticky top-17 z-50 rounded-t-none bg-danger-500 flex flex-row justify-center">
      <i class="text-2xl text-surface my-auto mr-1 pi pi-exclamation-triangle"></i>
      <p class="text-surface font-bold text-xl text-center pt-3">Project is currently locked by: {{ draw.project.lockedBy }}</p>
    </div>
    <div class="mx-2 mt-4 pl-3 bg-primary-500 dark:bg-primary-400 text-surface-50 font-bold flex flex-row rounded-lg">
      <div class="basis-1/2">
        <Button
          type="button"
          class="h-8 w-8 text-left dark:hover:bg-primary-400 text-surface-700 hover:text-surface-900 dark:text-white dark:hover:text-surface-300 transition-colors duration-200"
          text
          rounded
          @click="router.back()"
        >
          <i class="pi pi-chevron-left"></i>
        </Button>
      </div>

      <p class="my-auto pr-5 text-right basis-1/2 dark:text-surface-900">{{ draw?.userRole ? getRoleDisplayText(draw.userRole) : "" }} View</p>
    </div>
    <Panel class="mx-2 mb-0">
      <template #header>
        <div class="flex flex-row w-full text-sm">
          <div class="basis-1/2 h-fit my-auto text-lg">
            <!-- <LabelField label="LENDER" display="***NEED TO GET SITE NAME FROM API***"></LabelField> -->
            <LabelField label="PROJECT" :display="draw?.project.projectName"></LabelField>
            <LabelField
              label="PROJECT ID"
              :display="!isClient(NBH) || !entityIsInspector(draw?.userRole) ? draw?.project.projectCode : maskProjectCode(draw?.project.projectCode)"
            ></LabelField>
            <LabelField label="STATUS" :display="draw?.project.projectStatus.name"></LabelField>
            <div v-if="draw !== undefined && draw?.project.propertyAddress.length > 0">
              <p class="font-bold italic">ADDRESS:</p>
              <div class="flex flex-row">
                <p></p>
                <p>
                  {{ draw?.project.propertyAddress }}<br />
                  {{ draw?.project.propertyCity }},
                  {{ draw?.project.propertyState }}
                  {{ draw?.project.propertyZip }}
                </p>
                <Button
                  v-if="
                    draw != null &&
                    draw.project.propertyAddress.length > 0 &&
                    draw.project.propertyCity.length > 0 &&
                    draw.project.propertyZip != null &&
                    draw.project.propertyZip.length > 0
                  "
                  type="button"
                  icon="pi pi-map-marker"
                  rounded
                  outlined
                  class="h-8 w-8 my-auto ml-5"
                  @click="launchMap"
                ></Button>
              </div>
            </div>
            <p v-else>No Address Provided</p>
          </div>
          <div class="basis-1/2 text-right flex-col flex gap-2">
            <div v-if="entityIsBuilder(draw?.userRole)" class="ml-auto flex flex-row gap-2">
              <Button
                v-if="draw && draw.selectedDraw.drawNumber === 0 && submitEnabled && !itemsExist"
                label="Import Budget"
                icon="pi pi-file-import"
                class="h-9 font-bold mt-auto"
                :disabled="draw?.project.locked"
                @click="displayImportModal = true"
              >
              </Button>
            </div>
            <div v-if="entityIsInspector(draw?.userRole) && draw && !draw.selectedDraw.inspectionSubmitted" class="ml-auto flex flex-row gap-2">
              <SingleReportButton
                :report-id="'InspectionRequest'"
                :label="'Download Worksheet'"
                classes=" h-9 font-bold text-sm mt-auto mx-2"
                :project-id="draw?.project.projectId"
                :draw-id="draw?.selectedDraw.id"
                icon="calculator"
                :disabled="draw?.project.locked"
              />
              <Button
                v-if="draw && draw.selectedDraw.inspectionSubmitted"
                label="Current Draw Already Inspected"
                class="h-9 font-bold mt-auto"
                :disabled="true"
              >
              </Button>
              <FileUpload
                v-else
                name="fileupload"
                accept=".xls,.xlsx"
                :mode="'basic'"
                :auto="true"
                :custom-upload="true"
                :maxFileSize="propsStore.maxFileSize"
                :disabled="draw?.project.locked"
                @uploader="uploadFiles"
                choose-label="Upload Finished Worksheet"
                class="w-64 h-9 font-bold text-sm mt-auto"
              >
              </FileUpload>
            </div>
            <div class="ml-auto flex flex-row gap-2">
              <div v-if="showInfoCard()" class="flex flex-row gap-2">
                <div v-if="draw?.selectedDraw.drawStatus === DrawStatus.APPROVED && draw.selectedDraw.approved">
                  <label for="approvedDate" class="font-bold block text-center"> Approved Date </label>
                  <DatePicker
                    v-model="draw.selectedDraw.approved"
                    showIcon
                    iconDisplay="input"
                    inputId="approvedDate"
                    class="h-9"
                    inputClass="w-36"
                    panelClass="bg-white dark:bg-surface-700/80 backdrop-blur-xl"
                    :disabled="true"
                  />
                </div>
              </div>

              <div v-if="isEditable && entityIsInspector(draw?.userRole)">
                <label for="inspectionDate" class="font-bold block text-center"> Onsite Inspection Date </label>
                <DatePicker
                  v-model="inspectionDate"
                  manualInput
                  showIcon
                  iconDisplay="input"
                  inputId="inspectionDate"
                  class="h-9"
                  inputClass="w-36"
                  panelClass="bg-white dark:bg-surface-700/80 backdrop-blur-xl"
                  :maxDate="new Date()"
                  @date-select="fieldUpdate(ProjectField.INSPECTION_DATE)"
                  dateFormat="m/d/yy"
                  :disabled="draw?.project.locked"
                />
              </div>
              <Button
                v-if="draw && entityIsLender(draw.userRole) && draw.selectedDraw.drawStatus === DrawStatus.LENDER_PENDING"
                severity="success"
                :disabled="!isEditable"
                class="w-24 h-9 font-bold mt-auto"
                @click="() => approveDraw(true)"
                >Approve</Button
              >
              <Button
                v-if="draw && entityIsLender(draw.userRole) && draw.selectedDraw.drawStatus === DrawStatus.LENDER_PENDING"
                severity="danger"
                :disabled="!isEditable"
                class="w-24 h-9 font-bold mt-auto"
                @click="() => approveDraw(false)"
                >Decline</Button
              >
              <Button
                v-if="
                  submitEnabled &&
                  isEditable &&
                  draw?.selectedDraw.drawStatus !== DrawStatus.LENDER_PENDING &&
                  draw?.selectedDraw.drawStatus !== DrawStatus.COMPLETED
                "
                class="w-24 h-9 font-bold mt-auto"
                @click="clickSubmit()"
              >
                Submit
              </Button>
              <Button v-if="draw" class="w-auto h-9 font-bold mt-auto" :disabled="true">{{
                getDrawStatusDisplayText(draw.selectedDraw.drawStatus)
              }}</Button>
            </div>
            <div>
              <Select v-model="selectedDraw" :options="draws" option-label="name" scroll-height="80vh" class="w-36" @change="getProjectDraw"></Select>
            </div>
          </div>
        </div>
      </template>
      <div class="-my-3">
        <Tabs v-model:value="activeTab" class="text-lg border-surface-100 border-1 border-t-0 dark:border-surface-900 rounded-b-lg shadow">
          <TabList>
            <Tab v-for="(tab, index) in tabs" :value="index" v-show="tab.visible()">
              <i :class="tab.icon" class="mr-2"></i>
              <span class="font-bold">{{ tab.label }}</span>
            </Tab>
          </TabList>
        </Tabs>
        <div class="mt-3">
          <Textarea v-if="activeTab === 0" v-model="lenderNotes" placeholder="Lender Notes" class="w-full" disabled></Textarea>
          <Textarea
            v-if="activeTab === 1"
            v-model="inspectNotes"
            placeholder="Inspector Notes"
            class="w-full"
            :disabled="
              entityIsLender(draw?.userRole) || draw?.selectedDraw.drawNumber !== draw?.currentDraw || !submitEnabled || draw?.project.locked
            "
            @change="fieldUpdate(ProjectField.INSPECTION_NOTES)"
          ></Textarea>
          <Textarea
            v-if="activeTab === 2"
            v-model="drawNotes"
            placeholder="Draw Notes"
            class="w-full"
            :disabled="
              entityIsLender(draw?.userRole) || draw?.selectedDraw.drawNumber !== draw?.currentDraw || !submitEnabled || draw?.project.locked
            "
            @change="fieldUpdate(ProjectField.DRAW_NOTES)"
          ></Textarea>
          <div v-if="activeTab === 3 && draw">
            <FileManager :file-entity-type="FileEntity.DRAW" :files-url="drawFilesUrl" upload-mode="advanced" />
          </div>
        </div>
        <div v-if="activeTab === 4 && draw && summaryData">
          <ProjectSummary ref="projectSummary" :selectedDraw="draw.selectedDraw" :summaryData="summaryData" />
        </div>
      </div>
    </Panel>

    <ag-grid-vue
      v-show="width > 1250"
      theme="legacy"
      :rowData="drawItems"
      :rowClassRules="rowClassRules"
      :columnDefs="colDefs"
      :autoSizeStrategy="{ type: 'fitGridWidth' }"
      :defaultColDef="defaultColDef"
      :suppressCellFocus="true"
      :pinnedBottomRowData="footer"
      :singleClickEdit="true"
      :stopEditingWhenCellsLoseFocus="true"
      :suppressDragLeaveHidesColumns="true"
      domLayout="autoHeight"
      class="mx-2 py-3"
      :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
      @cell-editing-stopped="tableDrawUpdate"
      @row-data-updated="resize"
      @grid-size-changed="resize"
      @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
    >
    </ag-grid-vue>
    <Accordion v-show="width < 1250" multiple :activeIndex="[0]" class="mx-2 mt-4">
      <AccordionTab v-for="item in drawItems">
        <template #header>
          <div class="flex w-full gap-2">
            <div class="flex align-items-center gap-2 basis-1/2">
              <h1 class="h-fit my-auto font-bold">#{{ item.ordinal }}</h1>
              <h1 class="h-fit my-auto font-bold">{{ item.description }}</h1>
              <CustomTagRenderer
                class="h-8 my-auto"
                :params="{ value: item.customCostType, type: CustomTagType.CUSTOM_COST_TYPE }"
              ></CustomTagRenderer>
            </div>
            <div class="flex flex-row gap-2 basis-1/2 justify-end">
              <div class="flex flex-col text-right my-auto">
                <div v-if="cellIsEditable('thisDrawLoan', item)" class="text-success-500">Draw Loan: ${{ item.thisDrawLoan }}</div>
                <div v-if="cellIsEditable('thisDrawEquity', item)" class="text-success-500">Draw Loan: ${{ item.thisDrawEquity }}</div>
                <div v-if="cellIsEditable('thisDrawInspected', item)" class="text-success-500">Insp. Prior Draw: {{ item.priorDrawInspected }}%</div>
              </div>

              <i
                v-if="cellIsEditable('thisDrawLoan', item) || cellIsEditable('thisDrawEquity', item) || cellIsEditable('thisDrawInspected', item)"
                class="text-success-500 font-bold text-3xl my-auto pi pi-exclamation-circle"
              ></i>
            </div>
          </div>
        </template>
        <div class="flex flex-row gap-2 justify-between flex-wrap">
          <div class="flex flex-row gap-2">
            <div class="flex flex-col font-bold gap-1">
              <p class="h-8">Budget:</p>
              <p class="h-8">Adjust:</p>
              <p class="h-8">Total:</p>
            </div>
            <div class="flex flex-col gap-1">
              <p class="h-8">{{ formatCurrency(item.budget) }}</p>
              <p class="h-8">{{ formatCurrency(item.budgetAdj) }}</p>
              <p class="h-8">{{ formatCurrency(item.totalBudget) }}</p>
            </div>
          </div>

          <div class="flex flex-row gap-2">
            <div class="flex flex-col font-bold gap-1">
              <p class="h-8">Draw Loan:</p>
              <p class="h-8">Draw Equity:</p>
              <p class="h-8">Drawn To Date:</p>
            </div>
            <div class="flex flex-col gap-1">
              <InputNumber
                v-if="cellIsEditable('thisDrawLoan', item)"
                v-model="item.thisDrawLoan"
                prefix="$"
                :min="0"
                class="h-8 w-32"
                input-class="w-32 bg-secondary-500/30!"
                @update:model-value="() => drawUpdate(item)"
              ></InputNumber>
              <p v-else class="h-8">{{ formatCurrency(item.thisDrawLoan) }}</p>
              <InputNumber
                v-if="cellIsEditable('thisDrawEquity', item)"
                v-model="item.thisDrawEquity"
                prefix="$"
                :min="0"
                class="h-8 w-32"
                input-class="w-32 bg-secondary-500/30!"
                @update:model-value="() => drawUpdate(item)"
              ></InputNumber>
              <p v-else class="h-8">{{ formatCurrency(item.thisDrawEquity) }}</p>
              <p class="h-8">{{ formatCurrency(item.drawnToDate) }}</p>
            </div>
          </div>

          <div class="flex flex-row gap-2">
            <div class="flex flex-col font-bold gap-1">
              <p class="h-8">Insp. Prior Draw:</p>
              <InputNumber
                v-if="cellIsEditable('thisDrawInspected', item)"
                v-model="item.thisDrawInspected"
                suffix="%"
                :min="item.priorDrawInspected"
                :max="100"
                class="h-8 w-32"
                input-class="w-32 bg-secondary-500/30!"
                @update:model-value="() => drawUpdate(item)"
              ></InputNumber>
              <p v-else class="h-8">Insp. This Draw:</p>
              <p class="h-8">Total Insp:</p>
            </div>
            <div class="flex flex-col gap-1">
              <p class="h-8">{{ formatPercent(item.priorDrawInspected) }}</p>
              <p class="h-8">{{ formatPercent(item.thisDrawInspected) }}</p>
              <p class="h-8">{{ formatPercent(item.percentInspected) }}</p>
            </div>
          </div>

          <div class="flex flex-row gap-2">
            <div class="flex flex-col font-bold gap-1">
              <p class="h-8">Funds Available:</p>
              <p class="h-8">Percent Drawn:</p>
              <p class="h-8">Projected Over:</p>
            </div>
            <div class="flex flex-col gap-1">
              <p class="h-8">{{ formatCurrency(item.fundsAvail) }}</p>
              <p class="h-8">{{ formatPercent(item.percentDrawn) }}</p>
              <p class="h-8">{{ formatCurrency(item.projectedOver) }}</p>
            </div>
          </div>
        </div>
      </AccordionTab>
    </Accordion>
  </div>
  <TSDialog v-model:visible="displayImportModal" label="Import Budget" icon="file-import" size="w-[1000px]!">
    <template #content>
      <BudgetFileImportModal hide-inspection @import-complete="importBudget"></BudgetFileImportModal>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { useConfirm } from "primevue/useconfirm";
import { ProjectRole, getRoleDisplayText, entityIsInspector, entityIsLender, entityIsBuilder } from "@/enums/ProjectRole";
import {
  PortalProjectDetailSchema,
  type PortalProjectDetail,
  type PortalDrawItem,
  type ProjectDrawInfo,
  ProjectDrawInfoSchema,
} from "@/types/Projects";
import { DrawStatus, getDrawStatusDisplayText } from "@/enums/DrawStatus";
import {
  parseData,
  maskProjectCode,
  getCellEditableClass,
  percentFormatter,
  currencyFormatter,
  numberParser,
  openMap,
  confirmPopup,
  formatCurrency,
  formatPercent,
  round,
} from "@/services/Utility";
import { type Permissions, getPermissions } from "@/services/Permissions";
import { usePropsStore } from "@/stores/props";
import CostTypeRenderer from "@/components/cell-renderers/CostTypeRenderer.vue";
import SingleReportButton from "@/components/reports/SingleReportButton.vue";
import { ProjectField } from "@/enums/ProjectField";
import { isClient, NBH } from "@/services/Client";
import { FileEntity } from "@/enums/FileEntity";
import { useWindowSize } from "@vueuse/core";
import type { ApiError } from "@/types/Response";
import { CustomTagType } from "@/enums/CustomTagType";
import CustomTagRenderer from "@/components/cell-renderers/CustomTagRenderer.vue";
import type {
  CellClassParams,
  CellEditingStoppedEvent,
  EditableCallbackParams,
  GridApi,
  GridReadyEvent,
  GridSizeChangedEvent,
  RowClassParams,
  RowDataUpdatedEvent,
  ValueParserParams,
} from "ag-grid-community";
import z from "zod";
import type { SelectedDraw } from "@/types/Draw";
import type { DrawItem } from "@/types/DrawItem";

//TODO: Over (Under) column never seems to update in old accudraw
//TODO: Locked project view
//TODO: UPDATE ALL THE FOOTERS (EXCEPT BUDGET TABLE) TO COMPUTED STYLE

const { width } = useWindowSize();

const router = useRouter();
const confirm = useConfirm();

const gridApi = ref<GridApi>();
const activeTab = ref(0);
const lenderNotes = ref("");
const inspectNotes = ref("");
const inspectNotesDisabled = ref(true);
const drawNotes = ref("");
const selectedDraw = ref();
const draws = ref([{}]);
const draw = ref<PortalProjectDetail>();
const refreshDraw = ref<PortalProjectDetail>();
const drawItems = ref<PortalDrawItem[]>();
const loading = ref(true);
const isEditable = computed<boolean>(() => {
  if (draw.value == null) return false;

  if (draw.value.project.locked) return false;

  let ineditableStates;
  if (entityIsInspector(draw.value.userRole)) {
    ineditableStates = [DrawStatus.APPROVED, DrawStatus.DECLINED, DrawStatus.INSPECTED];
  } else if (entityIsLender(draw.value.userRole)) {
    ineditableStates = [DrawStatus.APPROVED, DrawStatus.DECLINED, DrawStatus.ACTIVE, DrawStatus.INSPECTED, DrawStatus.DRAWN];
  } else {
    ineditableStates = [DrawStatus.APPROVED, DrawStatus.DECLINED, DrawStatus.DRAWN];
  }

  return (
    draw.value.selectedDraw.drawNumber === draw.value.currentDraw &&
    !draw.value.project.locked &&
    ineditableStates.filter((status) => status === draw.value?.selectedDraw.drawStatus).length === 0
  );
});
const permissions = ref<Permissions>();
const submitEnabled = ref(false);
const inspectionDate = ref();
const projectSummary = ref();

const itemsExist = ref(false);
const budgetImported = ref(false);

const displayImportModal = ref(false);

const drawFilesUrl = computed<string>(() => {
  return draw.value ? `/draw-files/${draw.value.project.projectId}/${draw.value.selectedDraw.drawNumber}` : "";
});

const summaryData = ref<ProjectDrawInfo>();

const DrawEditSchema = z.object({
  drawItemId: z.string(),
  thisDrawLoan: z.number(),
  thisDrawEquity: z.number(),
  thisDrawInspected: z.number(),
});

const FieldEditSchema = z.object({
  inspection: z.coerce.date().nullable(),
  inspComments: z.string().nullable(),
  drawNotes: z.string().nullable(),
});

type DrawEdit = z.infer<typeof DrawEditSchema>;
type FieldEdit = z.infer<typeof FieldEditSchema>;

const currentEdits = ref<Map<string, DrawEdit>>(new Map());
const currentFieldEdits = ref<FieldEdit>({
  inspection: null,
  inspComments: null,
  drawNotes: null,
});

//TOTALS
const footer = ref([
  {
    ordinal: "Total",
    budget: 0,
    budgetAdj: 0,
    totalBudget: 0,
    thisDrawLoan: 0,
    thisDrawEquity: 0,
    drawnToDate: 0,
    fundsAvail: 0,
    projectedOver: 0,
    percentInspected: 0,
  },
]);

const tabs = ref([
  { label: "Lender Notes", icon: "pi pi-money-bill", visible: () => entityIsInspector(draw.value?.userRole) },
  {
    label: "Inspection Notes",
    icon: "pi pi-search",
    visible: () => draw.value?.userRole === ProjectRole.PRIMARY_INSPECTOR || draw.value?.userRole === ProjectRole.PRIMARY_LENDER,
  },
  { label: "Draw Notes", icon: "pi pi-pencil", visible: () => draw.value != null && !entityIsInspector(draw.value?.userRole) },
  { label: "Draw Files", icon: "pi pi-folder-open", visible: () => true },
  { label: "Summary", icon: "pi pi-chart-line", visible: () => true },
]);

const getProjectHeaderInfo = async (): Promise<void> => {
  await axios
    .get("/project/" + draw.value?.project.projectId + "/header")
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          parseData(ProjectDrawInfoSchema, data, summaryData);
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const approveDraw = (approved: boolean): void => {
  if (draw.value == null) return;
  const action = approved ? "approve" : "decline";

  axios
    .post(`/project/${draw.value.project.projectId}/draw/${draw.value.selectedDraw.drawNumber}/${action}`)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          if (draw.value) {
            if (approved) {
              draw.value.selectedDraw.drawStatus = DrawStatus.APPROVED;
              draw.value.selectedDraw.approved = new Date();
            } else {
              draw.value.selectedDraw.drawStatus = DrawStatus.DECLINED;
              draw.value.selectedDraw.approved = null;
            }
          }
          success("Draw Updated", "");
        },
        (errors: ApiError[]) => {
          error("Error Occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const refreshRow = (row: number): void => {
  if (refreshDraw.value == undefined || gridApi.value == null) return;
  const refreshItem = refreshDraw.value.selectedDraw.items[row];
  const rowData = gridApi.value.getDisplayedRowAtIndex(row);
  if (rowData == null) return;
  rowData.setDataValue("thisDrawLoan", refreshItem.thisDrawLoan);
  rowData.setDataValue("thisDrawEquity", refreshItem.thisDrawEquity);
  rowData.setDataValue("drawnToDate", refreshItem.drawnToDate);
  rowData.setDataValue("fundsAvail", refreshItem.fundsAvail);
  rowData.setDataValue("percentInspected", refreshItem.percentInspected);

  draw.value = refreshDraw.value;

  const footerData = gridApi.value.getPinnedBottomRow(0);
  if (footerData == null) return;
  let budget = 0;
  let budgetAdj = 0;
  let totalBudget = 0;
  let thisDrawLoan = 0;
  let thisDrawEquity = 0;
  let drawnToDate = 0;
  let fundsAvail = 0;
  let projectedOver = 0;
  refreshDraw.value?.selectedDraw.items.forEach((item) => {
    budget += item.budget;
    budgetAdj += item.budgetAdj;
    totalBudget += item.totalBudget;
    thisDrawLoan += item.thisDrawLoan;
    thisDrawEquity += item.thisDrawEquity;
    drawnToDate += item.drawnToDate;
    fundsAvail += item.fundsAvail;
    projectedOver += item.projectedOver;
  });
  footerData.setDataValue("budget", budget);
  footerData.setDataValue("budgetAdj", budgetAdj);
  footerData.setDataValue("totalBudget", totalBudget);
  footerData.setDataValue("thisDrawLoan", thisDrawLoan);
  footerData.setDataValue("thisDrawEquity", thisDrawEquity);
  footerData.setDataValue("drawnToDate", drawnToDate);
  footerData.setDataValue("fundsAvail", fundsAvail);
  footerData.setDataValue("projectedOver", projectedOver);
  footerData.setDataValue("percentInspected", refreshDraw.value ? refreshDraw.value.selectedDraw.percentComplete : 0);
  if (summaryData.value != null) {
    summaryData.value.percentComplete = refreshDraw.value ? refreshDraw.value.selectedDraw.percentComplete : 0;
  }
};

const showInfoCard = (): boolean => {
  if (draw.value?.selectedDraw.drawStatus === DrawStatus.LENDER_PENDING && !entityIsInspector(draw.value?.userRole)) {
    return false;
  }
  return (
    draw.value?.selectedDraw.drawSubmitted ||
    draw.value?.selectedDraw.inspectionSubmitted ||
    draw.value?.selectedDraw.drawStatus === DrawStatus.APPROVED ||
    entityIsInspector(draw.value?.userRole)
  );
};

const tableDrawUpdate = (event: CellEditingStoppedEvent): void => {
  if (event.newValue === event.oldValue) return;

  drawUpdate(event.data);
};

const drawUpdate = (item: PortalDrawItem): void => {
  const drawData: DrawEdit = {
    drawItemId: item.drawItemId,
    thisDrawLoan: item.thisDrawLoan,
    thisDrawEquity: item.thisDrawEquity,
    thisDrawInspected: item.thisDrawInspected,
  };

  currentEdits.value.set(item.drawItemId, drawData);
  applyCurrentEdits();
  refreshRow(item.ordinal - 1);
};

const fieldUpdate = (field: ProjectField): void => {
  if (draw.value == null) return;
  if (field === ProjectField.INSPECTION_DATE) {
    draw.value.selectedDraw.inspection = inspectionDate.value;
    currentFieldEdits.value.inspection = inspectionDate.value;
  } else if (field === ProjectField.INSPECTION_NOTES) {
    draw.value.selectedDraw.inspComments = inspectNotes.value;
    currentFieldEdits.value.inspComments = inspectNotes.value;
  } else if (field === ProjectField.DRAW_NOTES) {
    draw.value.selectedDraw.drawNotes = drawNotes.value;
    currentFieldEdits.value.drawNotes = drawNotes.value;
  }
};

const cellIsEditable = (field: string, data: PortalDrawItem): boolean => {
  if (
    !isEditable.value ||
    permissions.value === undefined ||
    data.drawItemId == null ||
    selectedDraw.value.drawNumber != draw.value?.project.currentDraw
  )
    return false;
  if (field === "thisDrawLoan" || field === "thisDrawEquity") return permissions.value.canEnterDrawAmountManually;
  if (field === "thisDrawInspected") {
    return (
      permissions.value.canEnterInspectionPercentagesManually &&
      draw.value?.selectedDraw.drawStatus !== DrawStatus.INSPECTED &&
      data.priorDrawInspected != 100 &&
      data.costType === "INSP"
    );
  }
  return false;
};

const rowClassRules = ref({
  "font-bold": (params: RowClassParams) => {
    return params.node.rowPinned != null;
  },
  "bg-danger-200 dark:bg-danger-800": (params: RowClassParams) => {
    return (
      !params.node.rowPinned &&
      !entityIsInspector(draw.value?.userRole) &&
      (params.data.projectedOver < 0 || params.data.totalBudget - params.data.drawnToDate < 0)
    );
  },
});

const calculateTotals = (): void => {
  footer.value[0].budget = 0;
  footer.value[0].budgetAdj = 0;
  footer.value[0].totalBudget = 0;
  footer.value[0].thisDrawLoan = 0;
  footer.value[0].thisDrawEquity = 0;
  footer.value[0].drawnToDate = 0;
  footer.value[0].fundsAvail = 0;
  footer.value[0].projectedOver = 0;
  footer.value[0].percentInspected = summaryData.value ? summaryData.value.percentComplete : 0;

  draw.value?.selectedDraw.items.forEach((item) => {
    footer.value[0].budget += item.budget;
    footer.value[0].budgetAdj += item.budgetAdj;
    footer.value[0].totalBudget += item.totalBudget;
    footer.value[0].thisDrawLoan += item.thisDrawLoan;
    footer.value[0].thisDrawEquity += item.thisDrawEquity;
    footer.value[0].drawnToDate += item.drawnToDate;
    footer.value[0].fundsAvail += item.fundsAvail;
    footer.value[0].projectedOver += item.projectedOver;
  });
};

const applyCurrentEdits = (): void => {
  if (draw.value == null || summaryData.value == null) return;
  const d: SelectedDraw = draw.value.selectedDraw;

  let aboveTotalBudgetDiff = 0;
  let thisTotalDrawLoanDiff = 0;
  let thisTotalDrawEquityDiff = 0;
  currentEdits.value.forEach((edit) => {
    const drawItem: PortalDrawItem | undefined = d.items.find((item) => item.drawItemId === edit.drawItemId);
    if (drawItem == null) return;
    if (entityIsInspector(draw.value?.userRole)) {
      drawItem.thisDrawInspected = edit.thisDrawInspected;
      drawItem.percentInspected = drawItem.thisDrawInspected > 0 ? drawItem.thisDrawInspected : drawItem.priorDrawInspected;
    } else {
      const thisDrawLoanDiff = edit.thisDrawLoan - drawItem.origDrawLoan;
      thisTotalDrawLoanDiff += thisDrawLoanDiff;
      const thisDrawEquityDiff = edit.thisDrawEquity - drawItem.origDrawEquity;
      thisTotalDrawEquityDiff += thisDrawEquityDiff;
      const budgetAdjustmentDiff = 0; // No need for budget adjustment yet

      const drawDiff = thisDrawLoanDiff + thisDrawEquityDiff;
      const totalDiff = drawDiff - budgetAdjustmentDiff;

      const prevAboveTotalBudget = Math.abs(drawItem.fundsAvail);
      const nowAboveTotalBudget = Math.abs(drawItem.fundsAvail - totalDiff);

      aboveTotalBudgetDiff += nowAboveTotalBudget - prevAboveTotalBudget;

      drawItem.thisDrawLoan = edit.thisDrawLoan;
      drawItem.thisDrawEquity = edit.thisDrawEquity;

      //Store here to calculate the differences
      drawItem.origDrawLoan = drawItem.thisDrawLoan;
      drawItem.origDrawEquity = drawItem.thisDrawEquity;

      drawItem.drawnToDateLoan = round(drawItem.thisDrawLoan + drawItem.priorDrawLoan, 2);
      drawItem.drawnToDateEquity = round(drawItem.thisDrawEquity + drawItem.priorDrawEquity, 2);
      drawItem.drawnToDate = round(drawItem.drawnToDateLoan + drawItem.drawnToDateEquity, 2);

      if (drawItem.totalBudget != 0) {
        if (drawItem.customCostType != null && drawItem.customCostType.startsWith("E"))
          drawItem.percentDrawn = round((drawItem.drawnToDateEquity / drawItem.totalBudget) * 100, 0);
        else drawItem.percentDrawn = round((drawItem.drawnToDate / drawItem.totalBudget) * 100, 0);
      }
    }
  });

  summaryData.value.difference = round(summaryData.value.difference - aboveTotalBudgetDiff, 2);
  summaryData.value.principalBalance = summaryData.value.principalBalance + thisTotalDrawLoanDiff;
  summaryData.value.equityBalance = summaryData.value.equityBalance + thisTotalDrawEquityDiff;

  if (summaryData.value.funds > 0) summaryData.value.percentDrawn = round((summaryData.value.fundsDisbursed * 100) / summaryData.value.funds, 0);
  else summaryData.value.percentDrawn = 0;

  let totalInspBudget = 0;
  let totalSiteDrawn = 0;
  draw.value.selectedDraw.items.forEach((item) => {
    if (item.costType.toUpperCase().startsWith("I")) {
      totalInspBudget += item.totalBudget;
      let mult = item.percentInspected > 0 ? item.percentInspected / 100 : 0;
      totalSiteDrawn += item.totalBudget * mult;
    }
  });

  summaryData.value.percentComplete = totalInspBudget > 0 ? round((totalSiteDrawn / totalInspBudget) * 100, 0) : 0;

  if (projectSummary.value) projectSummary.value.updatePercentages();
};

const drawSelected = async (): Promise<void> => {
  if (draw.value === undefined) return;
  drawItems.value = draw.value.selectedDraw.items;
  lenderNotes.value = draw.value.selectedDraw.inspInstructions;
  inspectNotesDisabled.value = entityIsLender(draw.value.userRole);
  permissions.value = getPermissions(draw.value.userRole);
  submitEnabled.value = isEditable.value;

  const isCurrentDraw = selectedDraw.value.drawNumber == draw.value.project.currentDraw;
  //There seems to be an error with zod where the string isn't getting coerced to a date, just do it here to get the year
  const dateParser = z.coerce.date().nullable();
  const inspection: Date | null = dateParser.parse(draw.value.selectedDraw.inspection);
  inspectionDate.value =
    isCurrentDraw && currentFieldEdits.value.inspection
      ? currentFieldEdits.value.inspection
      : inspection == null || inspection.getFullYear() == 1900
        ? new Date()
        : inspection;
  inspectNotes.value =
    isCurrentDraw && currentFieldEdits.value.inspComments ? currentFieldEdits.value.inspComments : draw.value.selectedDraw.inspComments;
  drawNotes.value =
    isCurrentDraw && currentFieldEdits.value.drawNotes
      ? currentFieldEdits.value.drawNotes
      : draw.value.selectedDraw.drawNotes
        ? draw.value.selectedDraw.drawNotes
        : "";

  await getProjectHeaderInfo();
  if (isCurrentDraw) applyCurrentEdits();

  calculateTotals();
  loading.value = false;
};

const getProjectDraw = (): void => {
  axios
    .get("/projects/" + draw.value?.project.projectId + "/" + selectedDraw.value.drawNumber)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          draw.value = data;
          drawSelected();
        },
        (errors: ApiError[]) => {
          error("Could not open draw", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const importBudget = async (importedItems: DrawItem[]) => {
  const newItems: PortalDrawItem[] = [];
  importedItems.forEach((item) => {
    const newItem: PortalDrawItem = {
      budget: item.budgetAmount,
      drawnToDate: item.drawnToDate,
      drawItemId: "",
      drawId: item.drawId,
      ordinal: item.ordinal,
      reference: item.lineRefNumber,
      description: item.description,
      costType: item.costType,
      customCostType: item.custCostType,
      budgetAdj: item.budgetAdjustment,
      totalBudget: item.adjustedBudget,
      priorDrawLoan: item.priorDrawnToDate,
      priorDrawEquity: item.priorEquityToDate,
      origDrawLoan: 0,
      origDrawEquity: 0,
      thisDrawLoan: item.totalThisDraw,
      thisDrawEquity: item.totalThisEquity,
      netDrawLoan: item.netThisDraw,
      netDrawEquity: item.netThisDrawEquity,
      drawnToDateLoan: item.drawnToDate,
      drawnToDateEquity: item.equityToDate,
      fundsAvail: item.fundsAvailable,
      percentDrawn: item.percentDrawn,
      percentInspected: item.percentInspected,
      thisDrawInspected: item.percentInspectedThisDraw,
      priorDrawInspected: item.percentInspectedPriorDraw,
      projectedOver: item.projectedOver,
    };
    newItems.push(newItem);
  });
  drawItems.value = newItems;
  budgetImported.value = true;
  displayImportModal.value = false;
};

const launchMap = (): void => {
  if (draw.value == null || draw.value.project.propertyZip == null) return;
  openMap(draw.value.project.propertyAddress, draw.value.project.propertyCity, draw.value.project.propertyZip);
};

const clickSubmit = (): void => {
  if (draw.value == null) return;
  if (entityIsInspector(draw.value.userRole)) {
    const date = inspectionDate.value != null ? inspectionDate.value : new Date();
    currentFieldEdits.value.inspection = date;
    confirmPopup(confirm, "Submit Inspection?", "Onsite Inspection Date: " + date.toDateString(), submitInspection, () =>
      info("Submit Cancelled", "")
    );
  } else {
    submitProject("");
  }
};

const submitInspection = (): void => {
  setTimeout(() => {
    submitProject("An inspection was submitted for " + inspectionDate.value.toDateString());
  }, 1000);
};

const submitProject = (message: string): void => {
  submitEnabled.value = false;
  if (budgetImported.value) submitProjectBudget();
  else submitProjectStandard(message);
};

const submitProjectBudget = (): void => {
  axios
    .post("/project/" + draw.value?.project.projectId + "/budget/submit", {
      currentFieldEdits: currentFieldEdits.value,
      drawItems: drawItems.value,
    })
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          currentEdits.value.clear();
          getProjectDraw();
          success("Submit Complete", "");
        },
        (errors: ApiError[]) => {
          error("Error", errors[0].message);
          submitEnabled.value = true;
        }
      );
    })
    .catch((e) => errorApi(e));
};

const submitProjectStandard = (message: string): void => {
  axios
    .post("/project/" + draw.value?.project.projectId + "/" + draw.value?.selectedDraw.drawNumber + "/submit", {
      currentEdits: Object.fromEntries(currentEdits.value),
      currentFieldEdits: currentFieldEdits.value,
    })
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          currentEdits.value.clear();
          getProjectDraw();
          success("Submit Complete", message);
        },
        (errors: ApiError[]) => {
          error("Error", errors[0].message);
          submitEnabled.value = true;
        }
      );
    })
    .catch((e) => errorApi(e));
};

const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const inspectParser = (params: ValueParserParams): number => {
  let input = Number(params.newValue);
  if (Number.isNaN(input)) return 0;
  input = input > 100 ? 100 : input < 0 ? 0 : input;
  if (input !== 0 && input < params.data.priorDrawInspected) {
    error("Invalid Value", "Value must be greater than the previous draw (" + params.data.priorDrawInspected + "%)");
    return params.oldValue;
  }
  return input;
};

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): void => {
  event.api.sizeColumnsToFit();
};

const changedFromPrevious = (field: string, ord: number): boolean => {
  if (draw.value) {
    const currentItem = draw.value.selectedDraw.items.find((i) => i.ordinal === ord) || {};
    let fieldKey = field as keyof typeof currentItem;
    return currentItem[fieldKey] > 0;
  } else {
    return true;
  }
};

const cellClass = (field: string, data: PortalDrawItem): string => {
  if (cellIsEditable(field, data)) return getCellEditableClass();
  else if (changedFromPrevious(field, data.ordinal)) return "font-bold italic text-warning-600 dark:text-warning-400";
  else return "";
};

const overUnderClass = (projectedOver: number): string => {
  if (projectedOver < 0) return "font-bold text-success-600 dark:text-success-400";
  else if (projectedOver > 0) return "font-bold text-danger-600 dark:text-danger-400";
  return "";
};

const defaultColDef = ref({
  resizable: true,
  autoHeight: true,
  autoHeaderHeight: true,
  wrapHeaderText: true,
  enableCellChangeFlash: true,
});

const colDefs = ref([
  { field: "ordinal", headerName: "#", cellDataType: "text", headerClass: "grid-align-center", cellClass: "text-center", width: 125 },
  { field: "description", width: 400 },
  {
    field: "costType",
    headerName: "Insp.",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    cellRenderer: CostTypeRenderer,
    width: 125,
  },
  {
    field: "customCostType",
    headerName: "Type",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    cellRenderer: CustomTagRenderer,
    cellRendererParams: {
      type: CustomTagType.SOURCE_TYPE,
    },
  },
  { field: "budget", valueFormatter: currencyFormatter },
  { field: "budgetAdj", headerName: "Adjust", valueFormatter: currencyFormatter },
  { field: "totalBudget", valueFormatter: currencyFormatter },
  {
    field: "thisDrawLoan",
    headerName: "Draw Loan",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params),
    editable: (params: EditableCallbackParams) => cellIsEditable("thisDrawLoan", params.data),
    cellClass: (params: CellClassParams) => cellClass("thisDrawLoan", params.data),
  },
  {
    field: "thisDrawEquity",
    headerName: "Drawn Equity",
    valueFormatter: currencyFormatter,
    valueParser: (params: ValueParserParams) => numberParser(params),
    editable: (params: EditableCallbackParams) => cellIsEditable("thisDrawEquity", params.data),
    cellClass: (params: CellClassParams) => cellClass("thisDrawEquity", params.data),
  },
  { field: "drawnToDate", headerName: "Drawn To Date", headerClass: "text-center", valueFormatter: currencyFormatter },
  { field: "fundsAvail", headerName: "Available", valueFormatter: currencyFormatter },
  { field: "percentDrawn", headerName: "% Drawn", valueFormatter: percentFormatter, width: 130 },
  { field: "priorDrawInspected", headerName: "Insp. Prior Draw", headerClass: "text-center", valueFormatter: percentFormatter, width: 130 },
  {
    field: "thisDrawInspected",
    headerName: "Insp. This Draw",
    headerClass: "text-center",
    valueFormatter: percentFormatter,
    valueParser: (params: ValueParserParams) => inspectParser(params),
    editable: (params: EditableCallbackParams) => cellIsEditable("thisDrawInspected", params.data),
    cellClass: (params: CellClassParams) => cellClass("thisDrawInspected", params.data),
    width: 130,
  },
  { field: "percentInspected", headerName: "Total Insp", headerClass: "text-center", valueFormatter: percentFormatter, width: 130 },
  {
    field: "projectedOver",
    headerName: "Over (Under)",
    valueFormatter: currencyFormatter,
    cellClass: (params: CellClassParams) => overUnderClass(params.data.projectedOver),
  },
]);

onMounted(async (): Promise<void> => {
  const route = useRoute();
  await loadData(route.params.id as string);

  for (let i = 0; i < tabs.value.length; i++) {
    if (tabs.value[i].visible()) {
      activeTab.value = i;
      break;
    }
  }
});

const loadData = async (projectId: string): Promise<void> => {
  await axios
    .get("/projects/" + projectId)
    .then((response) => {
      if (!parseData(PortalProjectDetailSchema, response.data.result, draw) || draw.value === undefined) return;
      let i = 0;
      draw.value.availableDraws.forEach((drawNum) => {
        draws.value[i] = { name: "Draw " + drawNum, drawNumber: drawNum };
        i++;
      });
      selectedDraw.value = draws.value[i - 1];
      itemsExist.value = draw.value.selectedDraw.items.length > 0;
      drawSelected();
    })
    .catch((e) => errorApi(e));
};

const uploadFiles = async (event: any): Promise<void> => {
  const data = new FormData();
  data.append("worksheet", event.files[0]);

  await axios
    .post("/projects/import-worksheet/" + draw.value?.project.projectId, data, { headers: { "Content-Type": "multipart/form-data" } })
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          success("Success", "Worksheet imported");
          const editMap: Map<string, DrawEdit> = new Map(Object.entries(data.currentEdits));
          parseData(z.map(z.string(), DrawEditSchema), editMap, currentEdits);
          parseData(FieldEditSchema, data.currentFieldEdits, currentFieldEdits);

          applyCurrentEdits();
          if (draw.value == null) return;
          if (currentFieldEdits.value.inspection) inspectionDate.value = currentFieldEdits.value.inspection;
          inspectNotes.value = currentFieldEdits.value.inspComments ? currentFieldEdits.value.inspComments : draw.value.selectedDraw.inspComments;
        },
        (errors: ApiError[]) => {
          error("Error", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};
</script>
